import { useContext, useEffect } from 'react';

import { PusherContext } from '@/contexts/pusher/pusher-provider';

export const usePusher = () => {
  const context = useContext(PusherContext);
  if (!context) {
    throw new Error('usePusher must be used within a PusherProvider');
  }
  return context.pusher;
};

/**
 * usePusherEvent abstracts event subscription for a given channel and event.
 * It subscribes to the channel, binds to the event, and cleans up by unbinding on unmount.
 *
 * @param channelName - The name of the channel to subscribe to.
 * @param eventName - The event name to bind the callback to.
 * @param callback - The event callback.
 * @param deps - Optional dependencies array to control when the effect refreshes.
 */
export const usePusherEvent = (
  channelName: string,
  eventName: string,
  callback: (data: any) => void,
  deps: any[] = []
) => {
  const pusher = usePusher();

  useEffect(() => {
    const channel = pusher.subscribe(channelName);
    channel.bind(eventName, callback);

    // Cleanup: unbind the event listener when the effect is cleaned up.
    return () => {
      channel.unbind(eventName, callback);
      // Optionally, unsubscribe from the channel if no longer needed:
      // pusher.unsubscribe(channelName);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pusher, channelName, eventName, ...deps]);
};
